import React from "react";
import { CheckCircle, Target, TrendingUp, Users } from "lucide-react";
import { cdnPath } from "@/utility/api";

const PlacementWhoShouldApply = () => {
  const criteria = [
    {
      icon: <Target className="w-6 h-6" />,
      text: "A 2024/2025 non-tech graduate struggling to get interview calls",
      highlight: "non-tech graduate",
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      text: "Looking for consulting, marketing, analyst, strategy or generalist roles",
      highlight: "consulting, marketing, analyst",
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      text: "Committed to completing weekly tasks and attending every session",
      highlight: "weekly",
    },
    {
      icon: <Users className="w-6 h-6" />,
      text: 'Done with "free webinars" and want real, hands-on guidance',
      highlight: "hands-on guidance",
    },
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-aurora-primary/5 via-transparent to-aurora-accent/5"></div>
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-aurora-primary/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-aurora-accent/10 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Side - Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-4xl lg:text-5xl font-bold  text-center bg-text-gradient bg-clip-text text-transparent">
                Who Should Apply
              </h2>
              <p className="text-lg text-center text-muted-foreground font-medium">
                This is for you if you're:
              </p>
            </div>

            {/* Criteria Cards */}
            <div className="space-y-4">
              {criteria.map((item, index) => (
                <div
                  key={index}
                  className="group flex items-start gap-4 p-6 bg-card/50 backdrop-blur-glass border border-border/50 rounded-2xl hover:bg-card/70 hover:border-aurora-primary/30 transition-all duration-300 hover:shadow-glow"
                >
                  <div className="flex-shrink-0 p-2 bg-aurora-primary/10 rounded-xl group-hover:bg-aurora-primary/20 transition-colors duration-300">
                    <div className="text-aurora-primary">{item.icon}</div>
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-base font-semibold text-left leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Visual Element */}
          <div className="relative">
            <div className="relative z-10 bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-glass border border-border/50 rounded-3xl p-8 lg:p-10">
              {/* Success Metrics */}
              <div className="text-center space-y-8">
                <div className="space-y-4">
                  <h3 className="text-2xl lg:text-3xl  font-bold text-foreground">
                    Join Our Success Story
                  </h3>
                  <p className="text-muted-foreground text-lg ">
                    Students placed in top firms
                  </p>
                </div>

                {/* Company Logos Grid */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {/* PwC Logo */}
                    <div className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:scale-105">
                      <div className="flex items-center justify-center h-8">
                        <img
                          src={`${cdnPath}/bcglogo.png`}
                          alt="BCG"
                          className="max-w-full max-h-full object-contain filter  hover:grayscale-0 transition-all duration-300"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextElementSibling.style.display = "flex";
                          }}
                        />
                        <div
                          className="hidden items-center justify-center w-full h-full text-gray-600 font-semibold text-sm"
                          style={{ display: "none" }}
                        >
                          BCG
                        </div>
                      </div>
                    </div>

                    {/* KPMG Logo */}
                    <div className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:scale-105">
                      <div className="flex items-center justify-center h-8">
                        <img
                          src={`${cdnPath}/kpmglogo.png`}
                          alt="KPMG"
                          className="max-w-full max-h-full object-contain filter hover:grayscale-0 transition-all duration-300"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextElementSibling.style.display = "flex";
                          }}
                        />
                        <div
                          className="hidden items-center justify-center w-full h-full text-gray-600 font-semibold text-sm"
                          style={{ display: "none" }}
                        >
                          KPMG
                        </div>
                      </div>
                    </div>

                    {/* ZS Associates Logo */}
                    <div className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:scale-105">
                      <div className="flex items-center justify-center h-8">
                        <img
                          src={`${cdnPath}/baincomplogo.png`}
                          alt="Bain and Company"
                          className="max-w-full max-h-full object-contain filter  hover:grayscale-0 transition-all duration-300"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextElementSibling.style.display = "flex";
                          }}
                        />
                        <div
                          className="hidden items-center justify-center w-full h-full text-gray-600 font-semibold text-xs"
                          style={{ display: "none" }}
                        >
                          Bain and Company
                        </div>
                      </div>
                    </div>

                    {/* Realme Logo */}
                    <div className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:scale-105">
                      <div className="flex items-center justify-center h-8">
                        <img
                          src={`${cdnPath}/deloittelogofinal.png`}
                          alt="Deloitte"
                          className="max-w-full max-h-full object-contain filter  hover:grayscale-0 transition-all duration-300"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextElementSibling.style.display = "flex";
                          }}
                        />
                        <div
                          className="hidden items-center justify-center w-full h-full text-gray-600 font-semibold text-sm"
                          style={{ display: "none" }}
                        >
                          Deloitte
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* "And many more" text */}
                  <div className="text-center -mt-1">
                    <p className="text-muted-foreground text-xs font-medium italic opacity-75">
                      and many more companies
                    </p>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="bg-gradient-to-br from-aurora-primary/10 to-aurora-primary/5 rounded-2xl p-6 text-center border border-aurora-primary/20">
                    <div className="text-3xl lg:text-4xl font-bold text-aurora-primary mb-2">
                      500+
                    </div>
                    <div className="text-sm text-muted-foreground font-medium">
                      Graduates Placed
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-aurora-accent/10 to-aurora-accent/5 rounded-2xl p-6 text-center border border-aurora-accent/20">
                    <div className="text-3xl lg:text-4xl font-bold text-aurora-accent mb-2">
                      1:1
                    </div>
                    <div className="text-sm text-muted-foreground font-medium">
                      Personal Mentoring
                    </div>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="pt-6">
                  <button
                    className="w-full bg-gradient-to-r from-aurora-primary to-aurora-accent text-white font-semibold py-4 px-8 rounded-2xl hover:shadow-glow transition-all duration-300 hover:scale-105 active:scale-95"
                    onClick={() =>
                      window.open("https://forms.gle/75EvB3WvKUGFTEvw8")
                    }
                  >
                    Start Your Journey Today
                  </button>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-aurora-primary/20 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-aurora-accent/20 rounded-full blur-xl animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlacementWhoShouldApply;
